import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { CheckCircle, ChevronRight, Clock, XCircle } from 'lucide-react';

import { useLanguage } from '../context/LanguageContext';
import { API_URL } from '../lib/api';

function PlayPage() {
  const { stageId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [stage, setStage] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [requiredQuestions, setRequiredQuestions] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [startTime] = useState(Date.now());
  const search = location.search || '';
  const queryParams = new URLSearchParams(search);
  const activeFilters = {
    subject_id: queryParams.get('subject_id') || '',
    form_name: queryParams.get('form_name') || '',
    chapter: queryParams.get('chapter') || ''
  };

  useEffect(() => {
    const fetchQuiz = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/stages/${stageId}/play${search}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStage(response.data.stage);
        setQuestions(response.data.questions || []);
        setRequiredQuestions(response.data.required_questions || response.data.questions?.length || 0);
        setTimeLeft(response.data.time_limit);
        setCurrentIndex(0);
        setAnswers({});
      } catch (error) {
        toast.error(language === 'zh' ? '\u65e0\u6cd5\u52a0\u8f7d\u6d4b\u9a8c' : 'Failed to load quiz');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchQuiz();
  }, [language, navigate, search, stageId]);

  useEffect(() => {
    if (timeLeft <= 0 || questions.length === 0) return undefined;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, questions.length]);

  const handleAnswer = (questionId, answerIndex) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answerIndex }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const stageBoardPath = stage?.level_num ? `/level/${stage.level_num}${search}` : '/dashboard';

  const handleCancel = () => {
    const hasProgress = Object.keys(answers).length > 0;
    const message = language === 'zh'
      ? '\u53d6\u6d88\u672c\u6b21\u7b54\u9898\u5e76\u8fd4\u56de\u9636\u6bb5\u677f\uff1f\u5df2\u9009\u62e9\u7684\u7b54\u6848\u4e0d\u4f1a\u4fdd\u5b58\u3002'
      : 'Cancel this attempt and return to the stage board? Selected answers will not be saved.';

    if (hasProgress && !window.confirm(message)) return;
    navigate(stageBoardPath);
  };

  const handleSubmit = async () => {
    if (submitting || questions.length === 0) return;
    setSubmitting(true);

    const token = localStorage.getItem('token');
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);

    try {
      const response = await axios.post(
        `${API_URL}/api/stages/${stageId}/submit`,
        { answers, time_spent: timeSpent, ...activeFilters },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      navigate(`/results/${stageId}`, {
        state: {
          result: response.data,
          stage,
          search
        }
      });
    } catch (error) {
      toast.error(language === 'zh' ? '\u65e0\u6cd5\u63d0\u4ea4\u6d4b\u9a8c' : 'Failed to submit quiz');
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-violet-50 to-pink-50">
        <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-violet-500"></div>
      </div>
    );
  }

  if (questions.length === 0 || questions.length < requiredQuestions) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-violet-50 via-white to-pink-50 px-4" data-testid="no-filtered-questions">
        <div className="max-w-md rounded-2xl border-2 border-zinc-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-2xl font-black text-zinc-900">
            {questions.length === 0
              ? (language === 'zh' ? '\u6ca1\u6709\u7b26\u5408\u6761\u4ef6\u7684\u9898\u76ee' : 'No Matching Questions')
              : (language === 'zh' ? '\u9898\u76ee\u6570\u91cf\u4e0d\u8db3' : 'Not Enough Questions')}
          </h1>
          <p className="mt-2 text-sm font-medium text-zinc-500">
            {questions.length === 0
              ? (language === 'zh'
                ? '\u8bf7\u8fd4\u56de\u5173\u5361\u9875\u8c03\u6574\u79d1\u76ee\u3001\u5e74\u7ea7\u6216\u7ae0\u8282\u7b5b\u9009\u3002'
                : 'Go back and adjust the Subject, Form, or Chapter filters for this stage.')
              : (language === 'zh'
                ? `此阶段需要 ${requiredQuestions} 题，目前只找到 ${questions.length} 题。请调整筛选或先新增题目。`
                : `This stage needs ${requiredQuestions} questions, but only ${questions.length} were found. Adjust the filters or add more questions.`)}
          </p>
          <button
            onClick={() => navigate(stageBoardPath)}
            className="mt-5 rounded-xl bg-violet-500 px-5 py-3 font-bold text-white hover:bg-violet-600"
          >
            {t('back')}
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const isAnswered = answers[currentQuestion?.id] !== undefined;
  const allAnswered = questions.every((q) => answers[q.id] !== undefined);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50" data-testid="play-page">
      <header className="sticky top-0 z-50 border-b-2 border-zinc-200 bg-white/80 px-4 py-4 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              onClick={handleCancel}
              disabled={submitting}
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 font-bold text-zinc-600 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="cancel-quiz-btn"
            >
              <XCircle className="h-5 w-5" />
              <span className="hidden sm:inline">{t('cancel')}</span>
            </button>
            <div>
              <span className="text-sm font-bold text-zinc-500">{t('question')}</span>
              <span className="ml-2 text-2xl font-black text-zinc-900">
                {currentIndex + 1} / {questions.length}
              </span>
            </div>
          </div>

          <div className={`flex items-center gap-2 rounded-xl px-4 py-2 font-bold ${
            timeLeft < 30 ? 'bg-red-100 text-red-600' : 'bg-violet-100 text-violet-600'
          }`}>
            <Clock className="h-5 w-5" />
            <span data-testid="timer">{formatTime(timeLeft)}</span>
          </div>
        </div>
      </header>

      <div className="h-1 bg-zinc-200">
        <div
          className="h-full bg-violet-500 transition-all"
          style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
        />
      </div>

      <main className="mx-auto max-w-3xl px-4 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6 rounded-2xl border-2 border-zinc-200 bg-white p-6 shadow-sm">
              {(currentQuestion?.story_board_en || currentQuestion?.story_board_zh) && (
                <div className="mb-5 rounded-xl border-l-4 border-purple-400 bg-gradient-to-br from-purple-50 to-violet-50 p-4">
                  <p className="mb-1.5 text-xs font-black uppercase tracking-wider text-purple-600">
                    {language === 'zh' ? '\u6545\u4e8b\u677f' : 'Story Board'}
                  </p>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-800 md:text-base" data-testid="story-board">
                    {(language === 'zh' && currentQuestion.story_board_zh) || currentQuestion.story_board_en || currentQuestion.story_board_zh}
                  </p>
                </div>
              )}

              <h2 className="mb-4 text-xl font-bold text-zinc-900 md:text-2xl" data-testid="question-text">
                {language === 'zh' ? currentQuestion?.text_zh : currentQuestion?.text_en}
              </h2>

              {currentQuestion?.image && (
                <img
                  src={currentQuestion.image}
                  alt=""
                  className="mb-4 max-h-72 w-full rounded-xl bg-zinc-50 object-contain"
                  data-testid="question-image"
                />
              )}
              {currentQuestion?.audio && (
                <audio controls src={currentQuestion.audio} className="mb-4 w-full" data-testid="question-audio" />
              )}

              <div className="space-y-3">
                {(language === 'zh' ? currentQuestion?.options_zh : currentQuestion?.options_en)?.map((option, index) => {
                  const isSelected = answers[currentQuestion?.id] === index;

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswer(currentQuestion.id, index)}
                      className={`w-full rounded-xl border-2 p-4 text-left font-medium transition-all ${
                        isSelected
                          ? 'border-violet-500 bg-violet-50 text-violet-700'
                          : 'border-zinc-200 hover:border-violet-300 hover:bg-violet-50/50'
                      }`}
                      data-testid={`option-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`h-3 w-3 flex-shrink-0 rounded-full transition-colors ${
                          isSelected ? 'bg-violet-500 ring-4 ring-violet-200' : 'bg-zinc-200'
                        }`} />
                        <span className="flex-1">{option}</span>
                        {isSelected && <CheckCircle className="h-5 w-5 text-violet-500" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="rounded-xl px-6 py-3 font-bold text-zinc-600 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('back')}
          </button>

          <div className="flex gap-2">
            {questions.map((q, i) => (
              <button
                key={q.id}
                onClick={() => setCurrentIndex(i)}
                className={`h-3 w-3 rounded-full transition-all ${
                  i === currentIndex
                    ? 'scale-125 bg-violet-500'
                    : answers[q.id] !== undefined
                      ? 'bg-green-500'
                      : 'bg-zinc-300'
                }`}
                aria-label={`${t('question')} ${i + 1}`}
              />
            ))}
          </div>

          {currentIndex === questions.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 px-6 py-3 font-bold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="submit-btn"
            >
              {submitting ? t('loading') : t('submit_answers')}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 rounded-xl bg-violet-500 px-6 py-3 font-bold text-white hover:bg-violet-600"
            >
              {t('next')}
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

export default PlayPage;
